import { useCallback, useEffect, useReducer, useRef } from 'react';

import { type IActions } from '../interface/IAction';
import {
	type ICaptureState,
	type IMergedSlot,
	type IPuEventQueueItem,
	type IRecordPuEventRequest,
	PU_EVENT_LABELS,
	type PuEventType,
	UNIT_CANCELLED,
} from '../interface/pu-events';
import { recordPuEvent, uploadEventPhoto } from '../lib/pu-events-api';
import { readQueue, removeQueueItem, upsertQueueItem } from '../lib/pu-events-store';
import { type CachedCoords, resolveUploadCoords } from '../lib/upload-location';

export const UNDO_SECONDS = 10;

type CaptureAction =
	| { type: 'ARM'; eventType: PuEventType; referenceId: string }
	| { type: 'TICK' }
	| { type: 'COMMIT' }
	| { type: 'SENT' }
	| { type: 'QUEUED_OFFLINE' }
	| { type: 'FAILED'; message: string; final?: boolean }
	| { type: 'UNDO' }
	| { type: 'RESET' };

export const initialCaptureState: ICaptureState = {
	phase: 'idle',
	eventType: null,
	referenceId: null,
	undoSecondsLeft: 0,
	lastError: null,
};

/** Pure reducer — exported for unit tests. */
export const captureReducer = (state: ICaptureState, action: CaptureAction): ICaptureState => {
	switch (action.type) {
		case 'ARM':
			if (state.phase !== 'idle') return state; // double-tap guard
			return {
				phase: 'armed',
				eventType: action.eventType,
				referenceId: action.referenceId,
				undoSecondsLeft: UNDO_SECONDS,
				lastError: null,
			};
		case 'TICK':
			if (state.phase !== 'armed') return state;
			return { ...state, undoSecondsLeft: Math.max(0, state.undoSecondsLeft - 1) };
		case 'COMMIT':
			if (state.phase !== 'armed' && state.phase !== 'failed') return state;
			return { ...state, phase: 'committing', undoSecondsLeft: 0, lastError: null, lastErrorFinal: false };
		case 'SENT':
			if (state.phase !== 'committing') return state;
			return { ...state, phase: 'sent', lastError: null };
		case 'QUEUED_OFFLINE':
			if (state.phase !== 'committing') return state;
			return { ...state, phase: 'queued-offline', lastError: null };
		case 'FAILED':
			if (state.phase !== 'committing') return state;
			return { ...state, phase: 'failed', lastError: action.message, lastErrorFinal: !!action.final };
		case 'UNDO':
			if (state.phase !== 'armed') return state; // undo only during the grace window
			return initialCaptureState;
		case 'RESET':
			return initialCaptureState;
		default:
			return state;
	}
};

export interface IPulseEventMachineParams {
	puCode: string | undefined;
	electionId: number | undefined;
	electionName: string | undefined;
	actions: IActions | undefined;
	locationCache?: CachedCoords | null;
	/** Queue contents changed (item added/updated/removed) — re-read it. */
	onQueueChanged: () => void;
	/** A capture was confirmed by the server — patch the checklist optimistically. */
	onConfirmed: (item: IPuEventQueueItem, mediaUrls?: string[]) => void;
	/** INEC cancelled the unit and the party has the report: nothing can be captured or attached. */
	paused?: boolean;
}

export interface IPulseEventMachine {
	state: ICaptureState;
	arm: (eventType: PuEventType) => void;
	undo: () => void;
	retry: () => void;
	dismiss: () => void;
	/** Upload + attach a photo to an already-captured slot (idempotent re-send). */
	attachPhoto: (slot: IMergedSlot, file: File, onProgress?: (percent: number) => void) => Promise<boolean>;
}

/**
 * The one-tap capture state machine: idle → armed (10s undo grace) →
 * committing → sent | queued-offline | failed. The referenceId is minted and
 * the queue item persisted at ARM time, so a crash mid-grace still leaves a
 * recoverable record; undo removes it. occurredAt is stamped at the moment of
 * the tap (the witnessed time), not at commit.
 */
export const usePulseEventMachine = (params: IPulseEventMachineParams): IPulseEventMachine => {
	const { puCode, electionId, electionName } = params;
	const [state, dispatch] = useReducer(captureReducer, initialCaptureState);

	// Reducer state in a ref for the imperative guards below.
	const stateRef = useRef(state);
	useEffect(() => {
		stateRef.current = state;
	});

	// The `actions` object from DataWrapper is a new reference every render —
	// keep everything callback-shaped in refs (documented CLAUDE.md gotcha).
	const actionsRef = useRef(params.actions);
	const locationCacheRef = useRef(params.locationCache);
	const onQueueChangedRef = useRef(params.onQueueChanged);
	const onConfirmedRef = useRef(params.onConfirmed);
	useEffect(() => {
		actionsRef.current = params.actions;
		locationCacheRef.current = params.locationCache;
		onQueueChangedRef.current = params.onQueueChanged;
		onConfirmedRef.current = params.onConfirmed;
	});

	const pausedRef = useRef(!!params.paused);
	useEffect(() => {
		pausedRef.current = !!params.paused;
	});

	const committingRef = useRef(false);

	const doCommit = useCallback(
		async (referenceId: string) => {
			if (!electionId || !puCode || committingRef.current) return;
			committingRef.current = true;
			dispatch({ type: 'COMMIT' });
			try {
				const item = readQueue(electionId, puCode).find((i) => i.referenceId === referenceId);
				if (!item) {
					dispatch({ type: 'FAILED', message: 'Capture record was lost — please tap again.' });
					return;
				}

				// GPS failure never blocks a capture — the timestamp is the
				// primary record; degraded coords are flagged instead.
				let request = item.request;
				if (request.location.latitude === '0' && request.location.longitude === '0') {
					const coords = await resolveUploadCoords(locationCacheRef.current);
					if (coords) {
						request = {
							...request,
							location: {
								address: '',
								latitude: String(coords.latitude),
								longitude: String(coords.longitude),
							},
							accuracy: coords.accuracy ?? 0,
						};
					}
				}
				const gpsDegraded = request.location.latitude === '0' && request.location.longitude === '0';
				upsertQueueItem(electionId, puCode, { ...item, request, gpsDegraded, status: 'sending' });
				onQueueChangedRef.current?.();

				if (typeof navigator !== 'undefined' && !navigator.onLine) {
					upsertQueueItem(electionId, puCode, { ...item, request, gpsDegraded, status: 'pending' });
					onQueueChangedRef.current?.();
					dispatch({ type: 'QUEUED_OFFLINE' });
					return;
				}

				const outcome = await recordPuEvent(request);
				if (outcome.reachable && outcome.httpStatus >= 200 && outcome.httpStatus < 300) {
					removeQueueItem(electionId, puCode, referenceId);
					onConfirmedRef.current?.({ ...item, request, gpsDegraded });
					dispatch({ type: 'SENT' });
					actionsRef.current?.notificationGlobal?.(`${PU_EVENT_LABELS[request.eventType]} captured`, true);
					return;
				}
				if (outcome.reachable && outcome.errorCode === UNIT_CANCELLED) {
					// Terminal — the server has the cancellation even if this phone hasn't synced it yet.
					removeQueueItem(electionId, puCode, referenceId);
					onQueueChangedRef.current?.();
					dispatch({
						type: 'FAILED',
						message: outcome.errorMessage || 'INEC cancelled this unit, so milestones can no longer be captured.',
						final: true,
					});
					return;
				}
				if (!outcome.reachable) {
					upsertQueueItem(electionId, puCode, { ...item, request, gpsDegraded, status: 'pending' });
					onQueueChangedRef.current?.();
					dispatch({ type: 'QUEUED_OFFLINE' });
					return;
				}
				upsertQueueItem(electionId, puCode, {
					...item,
					request,
					gpsDegraded,
					status: 'failed',
					retryCount: item.retryCount + 1,
				});
				onQueueChangedRef.current?.();
				dispatch({
					type: 'FAILED',
					message: outcome.errorMessage || `Could not record the event (HTTP ${outcome.httpStatus}). It is saved on this device.`,
				});
			} finally {
				committingRef.current = false;
			}
		},
		[electionId, puCode]
	);

	const doCommitRef = useRef(doCommit);
	useEffect(() => {
		doCommitRef.current = doCommit;
	});

	const arm = useCallback(
		(eventType: PuEventType) => {
			if (!electionId || !puCode || !electionName) return;
			if (pausedRef.current) return;
			if (stateRef.current.phase !== 'idle') return; // double-tap guard
			const referenceId =
				typeof crypto !== 'undefined' && 'randomUUID' in crypto
					? crypto.randomUUID()
					: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
			const request: IRecordPuEventRequest = {
				referenceId,
				poolingUnitCode: puCode,
				election: { id: electionId, name: electionName },
				eventType,
				occurredAt: new Date().toISOString(), // witnessed time = tap time
				location: { address: '', latitude: '0', longitude: '0' },
				accuracy: 0,
				mediaUrls: [],
				resultSource: 2,
				notes: '',
			};
			// Persisted as 'armed' (NOT counted as captured) so the undo grace
			// window survives a crash without the checklist advancing early.
			upsertQueueItem(electionId, puCode, {
				referenceId,
				request,
				capturedAt: request.occurredAt,
				status: 'armed',
				retryCount: 0,
			});
			onQueueChangedRef.current?.();
			dispatch({ type: 'ARM', eventType, referenceId });
		},
		[electionId, puCode, electionName]
	);

	const undo = useCallback(() => {
		const current = stateRef.current;
		if (current.phase !== 'armed' || !current.referenceId) return;
		if (electionId && puCode) {
			removeQueueItem(electionId, puCode, current.referenceId);
			onQueueChangedRef.current?.();
		}
		dispatch({ type: 'UNDO' });
	}, [electionId, puCode]);

	const retry = useCallback(() => {
		const current = stateRef.current;
		if (current.phase !== 'failed' || !current.referenceId || current.lastErrorFinal) return;
		void doCommitRef.current(current.referenceId);
	}, []);

	const dismiss = useCallback(() => dispatch({ type: 'RESET' }), []);

	// Undo countdown: tick every second while armed; commit at zero.
	useEffect(() => {
		if (state.phase !== 'armed') return;
		if (state.undoSecondsLeft <= 0) {
			if (state.referenceId) void doCommitRef.current(state.referenceId);
			return;
		}
		const timer = setInterval(() => dispatch({ type: 'TICK' }), 1_000);
		return () => clearInterval(timer);
	}, [state.phase, state.undoSecondsLeft, state.referenceId]);

	// Auto-return to idle after a successful or offline-queued capture so the
	// next event's CTA is immediately tappable.
	useEffect(() => {
		if (state.phase !== 'sent' && state.phase !== 'queued-offline') return;
		const timer = setTimeout(() => dispatch({ type: 'RESET' }), 2_500);
		return () => clearTimeout(timer);
	}, [state.phase]);

	const attachPhoto = useCallback(
		async (slot: IMergedSlot, file: File, onProgress?: (percent: number) => void): Promise<boolean> => {
			if (!electionId || !puCode || !electionName || !slot.referenceId) return false;
			if (pausedRef.current) return false;
			const coords = await resolveUploadCoords(locationCacheRef.current);
			const uri = await uploadEventPhoto(file, coords, onProgress);
			if (!uri) return false;

			// Idempotent replay: same referenceId + the new mediaUrl — the backend
			// merges it into the stored event ($addToSet) instead of duplicating.
			const request: IRecordPuEventRequest = {
				referenceId: slot.referenceId,
				poolingUnitCode: puCode,
				election: { id: electionId, name: electionName },
				eventType: slot.eventType,
				occurredAt: slot.occurredAt ?? new Date().toISOString(),
				location: slot.location ?? { address: '', latitude: '0', longitude: '0' },
				accuracy: slot.accuracy ?? 0,
				mediaUrls: [uri],
				resultSource: 2,
				notes: '',
			};
			const outcome = await recordPuEvent(request);
			if (outcome.reachable && outcome.httpStatus >= 200 && outcome.httpStatus < 300) {
				const merged = outcome.body?.data?.mediaUrls ?? [...slot.mediaUrls, uri];
				onConfirmedRef.current?.(
					{
						referenceId: slot.referenceId,
						request,
						capturedAt: request.occurredAt,
						status: 'pending',
						retryCount: 0,
					},
					merged
				);
				actionsRef.current?.notificationGlobal?.('Photo attached to the event', true);
				return true;
			}
			actionsRef.current?.notificationGlobal?.('Photo upload could not be attached — try again', false);
			return false;
		},
		[electionId, puCode, electionName]
	);

	return { state, arm, undo, retry, dismiss, attachPhoto };
};
